import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Navigate } from 'react-router-dom';
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import SyncIcon from '@mui/icons-material/Sync';
import dayjs from 'dayjs';
import { toast } from 'react-toastify';
import AuthContext from '../../context/authContext';
import useGetResponseMarketing from '../../hooks/useGetResponseMarketing';

function normalizeCampaignList(data) {
  if (Array.isArray(data)) return data;
  if (data && Array.isArray(data.campaigns)) return data.campaigns;
  return [];
}

function formatDate(value) {
  if (!value) return '—';
  try {
    return dayjs(value).format('DD.MM.YYYY HH:mm');
  } catch (_) {
    return '—';
  }
}

const TABLE_SKELETON_ROW_COUNT = 8;

function GetResponseMarketingTableSkeletonRows() {
  return (
    <>
      {Array.from({ length: TABLE_SKELETON_ROW_COUNT }, (_, i) => (
        <TableRow key={`gr-skeleton-${i}`}>
          <TableCell>
            <Skeleton variant='text' width='72%' animation='wave' />
          </TableCell>
          <TableCell sx={{ minWidth: 280 }}>
            <Skeleton variant='rounded' height={40} animation='wave' />
          </TableCell>
          <TableCell>
            <Skeleton variant='text' width={140} animation='wave' />
          </TableCell>
          <TableCell>
            <Skeleton variant='text' width='88%' animation='wave' />
          </TableCell>
          <TableCell align='right'>
            <Skeleton variant='rounded' width={72} height={32} sx={{ ml: 'auto' }} animation='wave' />
          </TableCell>
        </TableRow>
      ))}
    </>
  );
}

export default function GetResponseMarketingManagement() {
  const authCtx = useContext(AuthContext);
  const { getStatus, getCampaigns, patchCampaign, postSync } = useGetResponseMarketing();

  const canRead = authCtx.checkRight('getresponse:read');
  const canUpdate = authCtx.checkRight('getresponse:update');
  const canSync = authCtx.checkRight('getresponse:sync');

  const [rows, setRows] = useState([]);
  const [campaignOptions, setCampaignOptions] = useState([]);
  const [loadingData, setLoadingData] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [syncAllBusy, setSyncAllBusy] = useState(false);

  const load = useCallback(async () => {
    setLoadingData(true);
    try {
      const [statusRes, campaignsRes] = await Promise.all([getStatus(), getCampaigns()]);
      setRows(statusRes?.entertainments || []);
      setCampaignOptions(normalizeCampaignList(campaignsRes));
    } catch (e) {
      toast.error('Fehler beim Laden der GetResponse-Daten.');
      throw e;
    } finally {
      setLoadingData(false);
    }
  }, [getStatus, getCampaigns]);

  useEffect(() => {
    if (!canRead) return;
    load().catch(() => {});
  }, [canRead, load]);

  const optionById = useMemo(() => {
    const m = new Map();
    for (const c of campaignOptions) {
      const id = c.campaignId || c.campaignID || c.id;
      if (id) m.set(String(id), c);
    }
    return m;
  }, [campaignOptions]);

  const handleCampaignChange = async (entertainment, newIds) => {
    if (!canUpdate) return;
    setBusyId(entertainment._id);
    try {
      const updatedEntertainment = await patchCampaign(entertainment._id, newIds);
      setRows(prev =>
        prev.map(row =>
          row._id === entertainment._id
            ? {
                ...row,
                ...updatedEntertainment,
                getResponseCampaignIds: updatedEntertainment?.getResponseCampaignIds || newIds,
                getResponseCampaignId: updatedEntertainment?.getResponseCampaignId || newIds[0] || '',
              }
            : row
        )
      );
      toast.success('Kampagnen gespeichert.');
    } catch (e) {
      toast.error('Fehler beim Speichern der Kampagnen-Zuordnung.');
      throw e;
    } finally {
      setBusyId(null);
    }
  };

  const handleSyncOne = async id => {
    if (!canSync) return;
    setBusyId(id);
    try {
      await postSync(id);
      toast.success('Synchronisation gestartet und abgeschlossen.');
      await load();
    } catch (e) {
      if (e?.code === 'SYNC_IN_PROGRESS') {
        toast.warn('Eine andere Synchronisation läuft bereits.');
      } else if (e?.code === 'GETRESPONSE_NOT_CONFIGURED') {
        toast.error('GetResponse ist auf dem Server nicht konfiguriert.');
      } else {
        toast.error('Fehler bei der Synchronisation.');
      }
      throw e;
    } finally {
      setBusyId(null);
    }
  };

  const handleSyncAll = async () => {
    if (!canSync) return;
    setSyncAllBusy(true);
    try {
      await postSync();
      toast.success('Globale Synchronisation abgeschlossen.');
      await load();
    } catch (e) {
      if (e?.code === 'SYNC_IN_PROGRESS') {
        toast.warn('Eine andere Synchronisation läuft bereits.');
      } else if (e?.code === 'GETRESPONSE_NOT_CONFIGURED') {
        toast.error('GetResponse ist auf dem Server nicht konfiguriert.');
      } else {
        toast.error('Fehler bei der globalen Synchronisation.');
      }
      throw e;
    } finally {
      setSyncAllBusy(false);
    }
  };

  if (!canRead) {
    return <Navigate to='/welcome' replace />;
  }

  return (
    <Box sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant='h5'>GetResponse Marketing</Typography>
        {canSync && (
          <Button
            variant='contained'
            startIcon={syncAllBusy ? <CircularProgress size={18} color='inherit' /> : <SyncIcon />}
            disabled={syncAllBusy || loadingData}
            onClick={() => handleSyncAll().catch(() => {})}
          >
            Alle synchronisieren
          </Button>
        )}
      </Box>

      <Table size='small'>
        <TableHead>
          <TableRow>
            <TableCell>Entertainment</TableCell>
            <TableCell>GetResponse-Kampagnen</TableCell>
            <TableCell>Zuletzt gesynct</TableCell>
            <TableCell>Ergebnis / Fehler</TableCell>
            <TableCell align='right'>Aktion</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {loadingData ? (
            <GetResponseMarketingTableSkeletonRows />
          ) : (
            rows.map(row => {
              const sync = row.getResponseMarketingSync || {};
              const currentIds = Array.isArray(row.getResponseCampaignIds)
                ? row.getResponseCampaignIds.map(String).filter(Boolean)
                : [];
              if (currentIds.length === 0 && row.getResponseCampaignId) {
                currentIds.push(String(row.getResponseCampaignId));
              }
              const selected = currentIds.map(id =>
                optionById.has(id) ? optionById.get(id) : { campaignId: id, name: id }
              );

              return (
                <TableRow key={row._id}>
                  <TableCell>{row.longName || row.entertainmentName}</TableCell>
                  <TableCell sx={{ minWidth: 280 }}>
                    <Autocomplete
                      multiple
                      size='small'
                      options={campaignOptions}
                      disabled={!canUpdate || busyId === row._id}
                      value={selected}
                      onChange={(_, v) => {
                        const ids = (Array.isArray(v) ? v : [])
                          .map(item => item?.campaignId || item?.campaignID || item?.id)
                          .filter(Boolean)
                          .map(String);
                        handleCampaignChange(row, ids).catch(() => {});
                      }}
                      getOptionLabel={opt =>
                        opt ? `${opt.name || '—'} (${opt.campaignId || opt.campaignID || opt.id || '—'})` : ''
                      }
                      isOptionEqualToValue={(a, b) =>
                        String(a?.campaignId || a?.campaignID || a?.id || '') ===
                        String(b?.campaignId || b?.campaignID || b?.id || '')
                      }
                      renderInput={params => <TextField {...params} label='Kampagnen' placeholder='Suchen…' />}
                    />
                  </TableCell>
                  <TableCell>{formatDate(sync.lastSuccessAt || sync.lastAttemptAt)}</TableCell>
                  <TableCell>
                    <Typography variant='body2' sx={{ whiteSpace: 'pre-wrap' }}>
                      {sync.lastResultSummary || sync.lastError || '—'}
                    </Typography>
                  </TableCell>
                  <TableCell align='right'>
                    {canSync && (
                      <Button
                        size='small'
                        variant='outlined'
                        startIcon={busyId === row._id ? <CircularProgress size={16} /> : <SyncIcon />}
                        disabled={busyId === row._id || syncAllBusy}
                        onClick={() => handleSyncOne(row._id).catch(() => {})}
                      >
                        Sync
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              );
            })
          )}
        </TableBody>
      </Table>
    </Box>
  );
}
